import { useSortable } from "@dnd-kit/react/sortable";
import convert from "convert-units";
import lround from "lodash/round";
import { PARAMETER_OPTIONS } from "../../../constants";
import { repsValue } from "../../../utils/reps-range";
import { rpeValue } from "../../../utils/rpe-range";
import { mapParameter } from "../../other-exercise/CustomParametersSets";
import SvgIcon from "../../SvgIcon";
import { getExerciseName } from "../learn-more/blockHelpers";

const { RPE, RPEAMRAP, LOAD, REPS, FREEFORM } = PARAMETER_OPTIONS;

// Fixed sets/reps/rpe/load/%1RM layout (primary exercises, no custom
// parameter list) — matches AddExerciseSets' default path. AMRAP always
// shows in the RPE/RiR slot, never in reps, regardless of whether a reps
// target is also set.
const formatReps = (set) => {
  const value = repsValue(set.reps, set.repsLower);
  return value === "-" ? "—" : value;
};

// A complex card reads like the session table: each member's reps for the
// round, joined with "+".
const formatComplexReps = (members, rowIndex) =>
  members
    .map((member) => member.sets?.[rowIndex]?.reps)
    .filter((value) => value != null && value !== "")
    .join("+") || "—";

const formatRpe = (set, isRiR) =>
  rpeValue(set.rpe, set.rpeLower, isRiR) || "—";

// Custom parameter list (assistant exercises with an explicit RPE/AMRAP
// slot) — matches CustomParametersSets, which shows AMRAP in the
// RPE/AMRAP column unconditionally and never overloads the reps column.
const formatCustomReps = (set) => repsValue(set.reps, set.repsLower);

const formatCustomRpe = (set, isRiR) =>
  rpeValue(set.rpe, set.rpeLower, isRiR) || "—";

const formatLoad = (set, units, round) => {
  if (set.loadDrop) {
    const drop = parseFloat(set.loadDrop);
    return `${drop > 0 ? "+" : ""}${drop}%`;
  }
  const kg = parseFloat(set.load);
  if (!kg) return "—";
  const value = units === "imperial" ? convert(kg).from("kg").to("lb") : kg;
  return `${lround(value, round ? 0 : 2)} ${units === "imperial" ? "lb" : "kg"}`;
};

const formatPercentRM = (set) => {
  const percent = parseFloat(set.percentRepMax);
  return percent ? `${lround(percent, 1)}%` : "—";
};

// Edit Workout tags, keyed by itemState / card flags.
const TAGS = {
  edited: "wq-tag-edited",
  new: "wq-tag-new",
  removed: "wq-tag-removed",
  rejected: "wq-tag-removed",
  logged: "wq-tag-logged",
  metcon: "wq-tag-neutral",
  complex: "wq-tag-neutral",
};

/**
 * One queued exercise in the workout-queue panel: drag handle, superset
 * label tag, name, a compact set table and edit/remove controls. In Edit
 * Workout (`tags` set) it also shows the card's change tag, a removed card
 * offers Restore, and locked cards can't be dragged or removed.
 */
function WorkoutQueueCard({
  item,
  index,
  label,
  isEditing,
  inSuperset,
  activeUser,
  intensityMeasure = "RPE",
  isRiR = false,
  editingSet,
  highlighted,
  tags = null,
  canRemove = true,
  canDrag = true,
  onEdit,
  onRemove,
  t,
}) {
  const { ref, handleRef, isDragging } = useSortable({
    id: item.id,
    index,
    disabled: !canDrag,
  });

  // Edit Workout cards show their saved exercise until they have a draft.
  const exercise = item.exercise || item.original;
  const units = activeUser?.units;
  const round = activeUser?.round;
  // Stable-enough row keys without leaning on the map index (set lines are
  // display-only and never reorder inside a card).
  const setRows = (exercise.sets || []).map((set, i) => ({
    set,
    key: `set-${i}`,
    reps: item.complexMembers
      ? formatComplexReps(item.complexMembers, i)
      : formatReps(set),
  }));

  // Exercises added with a custom parameter list (see AddExerciseSets /
  // CustomParametersSets) don't use the default sets/reps/rpe/load/%1RM
  // columns — they define their own ordered set of fields per set.
  const parameters =
    exercise.primary === "assistant" && exercise.parameters?.length
      ? exercise.parameters
      : null;
  // Free-form is exclusive of the other three parameter slots (always
  // queued as ["Free-Form", "None", "None", "None"] — see
  // ParametersInputComponent). Mirrors ExerciseClipboardContent, which
  // also skips the column header and just prints the free-text value.
  const hasFreeForm = parameters?.includes(FREEFORM);
  const columnCount = hasFreeForm ? 1 : parameters ? parameters.length : 5;
  const gridStyle = { gridTemplateColumns: `repeat(${columnCount}, 1fr)` };

  const formatParameterValue = (set, param) => {
    if (param === LOAD) return formatLoad(set, units, round);
    if (param === REPS) return formatCustomReps(set);
    if ([RPE, RPEAMRAP].includes(param)) return formatCustomRpe(set, isRiR);
    return set[mapParameter(param)] ?? "—";
  };

  return (
    <div
      ref={ref}
      className={`wq-card${inSuperset ? " wq-card-in-superset" : ""}${
        isEditing ? " wq-card-editing" : ""
      }${isDragging ? " wq-card-dragging" : ""}${
        highlighted ? " commit-highlight" : ""
      }${item.removed ? " wq-card-removed" : ""}`}
    >
      <div className="wq-card-top">
        <span className="calendar-prefix">{label}</span>
        {tags ? (
          <button
            type="button"
            className="wq-card-name wq-card-name-button"
            onClick={onEdit}
            disabled={editingSet || !!item.complexIds}
            aria-current={isEditing ? "true" : undefined}
          >
            {getExerciseName(exercise)}
            {tags.map((tag) => (
              <span key={tag} className={`wq-tag ${TAGS[tag]}`}>
                {t(`editWorkout.tags.${tag}`)}
              </span>
            ))}
          </button>
        ) : (
          <span className="wq-card-name">{getExerciseName(exercise)}</span>
        )}
        <div className="wq-card-actions">
          {canDrag && (
            <button
              ref={handleRef}
              type="button"
              className="wq-icon-btn wq-drag-handle"
              title={t("workoutQueue.dragToReorder")}
            >
              <SvgIcon src="/img/new-icons/drag-outline.svg" noFilterOnLight />
            </button>
          )}
          {!item.complexIds && (
            <button
              type="button"
              className="wq-icon-btn wq-icon-btn-edit"
              onClick={onEdit}
              disabled={editingSet}
              title={t("workoutQueue.editExercise")}
            >
              <SvgIcon src="/img/new-icons/edit-pencil.svg" />
            </button>
          )}
          {canRemove && (
            <button
              type="button"
              className="wq-icon-btn wq-icon-btn-danger"
              onClick={onRemove}
              title={
                item.removed
                  ? t("editWorkout.restore")
                  : t("workoutQueue.removeExercise")
              }
            >
              <SvgIcon
                src={`/img/new-icons/${
                  item.removed ? "vuesax-linear-refresh-2" : "cross"
                }.svg`}
              />
            </button>
          )}
        </div>
      </div>

      <div className="wq-card-sets">
        {!hasFreeForm && (
          <div className="wq-set-row wq-set-head" style={gridStyle}>
            {parameters ? (
              parameters.map((param) => (
                <span key={param}>
                  {[RPE, RPEAMRAP].includes(param) ? intensityMeasure : param}
                </span>
              ))
            ) : (
              <>
                <span>{t("workoutQueue.sets")}</span>
                <span>{t("workoutQueue.reps")}</span>
                <span>{intensityMeasure}</span>
                <span>{t("workoutQueue.load")}</span>
                <span>{t("workoutQueue.percentRm")}</span>
              </>
            )}
          </div>
        )}
        {setRows.map(({ set, key, reps }) => (
          <div className="wq-set-row" key={key} style={gridStyle}>
            {hasFreeForm ? (
              <span className="wq-freeform">{set.freeform || "—"}</span>
            ) : parameters ? (
              parameters.map((param) => (
                <span key={`${key}-${param}`}>
                  {formatParameterValue(set, param)}
                </span>
              ))
            ) : (
              <>
                <span>{set.sets ?? "—"}</span>
                <span>{reps}</span>
                <span>{formatRpe(set, isRiR)}</span>
                <span>{formatLoad(set, units, round)}</span>
                <span>{formatPercentRM(set)}</span>
              </>
            )}
          </div>
        ))}
      </div>

      {isEditing && (
        <div className="wq-editing-hint">
          {t("workoutQueue.editingInModal")}
        </div>
      )}
    </div>
  );
}

export default WorkoutQueueCard;
